import axios from 'axios';
import { isAuthenticated, getToken, logout } from './auth';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:8000/api'
  : '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true, // Important for CORS
});

api.interceptors.request.use((config) => {
  try {
    if (isAuthenticated()) {
      const token = getToken();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
  } catch (error) {
    console.error('Error retrieving token', error);
  }
  return config;
});

// Add response interceptor for debugging
api.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error);
    if (error.response?.status === 401) {
      logout();
    }
    return Promise.reject(error);
  }
);

export default api;

export const fetchConversations = (limit = 25, offset = 0) =>
  api
    .get(`/conversations?limit=${limit}&offset=${offset}`)
    .then((response) => response.data);
export const fetchConversationDetails = (id) =>
  api.get(`/conversations/${id}`).then((response) => response.data);
export const updateConversationStatus = (id, payload) =>
  api
    .put(`/conversations/${id}/set-status`, payload)
    .then((response) => response.data);
export const updateConversationShortNote = (id, payload) =>
  api
    .put(`/conversations/${id}/short-note`, payload)
    .then((response) => response.data);
export const updateConversationSummary = (id, payload) =>
  api
    .put(`/conversations/summary/${id}`, payload)
    .then((response) => response.data);
export const triggerNewSummary = (id, payload) =>
  api
    .post(`/conversations/${id}/trigger-new-summary`, payload)
    .then((response) => response.data);
export const fetchAudioFile = (audioFilename) => {
  const url = `/audio/${audioFilename}`;
  return api.get(url, {
    responseType: 'blob', // Ensure the response is treated as a file (binary data)
    headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate"
    }
  });
};
export const deleteConversation = (id) =>
  api.delete(`/conversations/${id}`).then((response) => response.data);
export const fetchPromptTemplates = () =>
  api.get(`/prompt-templates`).then((response) => response.data);

export const fetchPromptTemplatesWithPlan = () =>
  api.get(`/prompt-templates-with-plan`).then((response) => response.data);

export const createPromptTemplate = (templateData) =>
  api.post(`/prompt-templates`, templateData).then((response) => response.data);

export const updatePromptTemplate = (templateId, templateData) =>
  api.put(`/prompt-templates/${templateId}`, templateData).then((response) => response.data);

export const deletePromptTemplate = (templateId) =>
  api.delete(`/prompt-templates/${templateId}`).then((response) => response.data);

export const fetchCustomPhrases = () =>
  api.get('/custom-phrases').then((response) => response.data);

export const updateCustomPhrases = (phrases) =>
  api.post('/custom-phrases', phrases).then((response) => response.data);
